import { openJson, sealJson } from './crypto';
import type { WritePolicy } from './policy';
import { offlineStore } from './store';
import { currentDataKey } from './vault';

/**
 * The queue of writes captured while the device had no connection.
 *
 * Order is the queue's whole contract: a draft created, edited and then submitted has to reach
 * the server in that sequence or the later requests address a document that does not exist yet.
 * So identifiers are built to sort chronologically and the store hands them back in key order.
 */

const SEQUENCE_KEY = 'outbox-sequence';

export type OutboxState =
  /** Waiting for a connection, or waiting its turn. */
  | 'pending'
  /** The server was reachable but could not answer; it will be tried again. */
  | 'failed'
  /** The server refused it on its merits. Only a person can decide what to do next. */
  | 'rejected'
  /** It was dispatched and the reply was lost, so whether it landed is unknown. */
  | 'uncertain';

export interface OutboxError {
  readonly status: number;
  readonly code: string | null;
}

export interface OutboxRecord {
  readonly id: string;
  readonly version: 1;
  readonly createdAt: string;
  readonly userId: string;
  readonly tenantId: string | null;
  readonly method: 'POST' | 'PUT' | 'DELETE';
  readonly path: string;
  readonly kind: string;
  readonly labelKey: string;
  readonly state: OutboxState;
  readonly attempts: number;
  readonly lastAttemptAt: string | null;
  /** Set after a server-side failure so a flush backs off instead of hammering. */
  readonly nextAttemptAt: string | null;
  /**
   * Status and error code only. The server's prose is not kept: the code is what the interface
   * translates, and a message could carry a fragment of the document back out of the seal.
   */
  readonly lastError: OutboxError | null;
  readonly sealedBody: string | null;
  readonly sealedDetail: string | null;
  /**
   * Sent as `Idempotency-Key` on every attempt, and stable across retries. Where the endpoint
   * files results under it, a replay returns the first outcome instead of writing twice.
   */
  readonly idempotencyKey: string;
}

/** The little that is shown about a queued write, once the vault is open to read it. */
export interface QueuedDetail {
  readonly description?: string;
  readonly postingDate?: string;
  readonly lineCount?: number;
  readonly amount?: number;
  readonly currencyCode?: string;
}

interface JournalLikeBody {
  description?: unknown;
  postingDate?: unknown;
  currencyCode?: unknown;
  lines?: unknown;
}

/** Describes a queued write from its own payload, so the sync centre can name it. */
export function summarizeWrite(body: unknown): QueuedDetail | null {
  if (typeof body !== 'object' || body === null) return null;
  const candidate = body as JournalLikeBody;
  const lines = Array.isArray(candidate.lines) ? candidate.lines : null;
  const detail: {
    description?: string;
    postingDate?: string;
    lineCount?: number;
    amount?: number;
    currencyCode?: string;
  } = {};

  if (typeof candidate.description === 'string') detail.description = candidate.description;
  if (typeof candidate.postingDate === 'string') detail.postingDate = candidate.postingDate;
  if (typeof candidate.currencyCode === 'string') detail.currencyCode = candidate.currencyCode;
  if (lines !== null) {
    detail.lineCount = lines.length;
    detail.amount = lines.reduce((total: number, line) => {
      const debit = (line as { debitAmount?: unknown }).debitAmount;
      return total + (typeof debit === 'number' ? debit : 0);
    }, 0);
  }

  return Object.keys(detail).length === 0 ? null : detail;
}

async function nextSequence(): Promise<number> {
  const current = await offlineStore().get<number>('meta', SEQUENCE_KEY) ?? 0;
  const next = current + 1;
  await offlineStore().put('meta', SEQUENCE_KEY, next);
  return next;
}

/** Sorts chronologically as a string, which is what the store orders by. */
function outboxId(createdAt: number, sequence: number): string {
  return `${String(createdAt).padStart(15, '0')}-${String(sequence).padStart(6, '0')}`;
}

export class OutboxSealedError extends Error {
  constructor() {
    super('The offline queue is sealed: unlock the offline copy first.');
    this.name = 'OutboxSealedError';
  }
}

export interface EnqueueInput {
  readonly userId: string;
  readonly tenantId: string | null;
  readonly policy: WritePolicy;
  readonly path: string;
  readonly body: unknown;
}

export async function enqueueWrite(input: EnqueueInput): Promise<OutboxRecord> {
  const key = currentDataKey();
  if (key === null) throw new OutboxSealedError();

  const now = Date.now();
  const detail = summarizeWrite(input.body);
  const record: OutboxRecord = {
    id: outboxId(now, await nextSequence()),
    version: 1,
    createdAt: new Date(now).toISOString(),
    userId: input.userId,
    tenantId: input.tenantId,
    method: input.policy.method,
    path: input.path,
    kind: input.policy.kind,
    labelKey: input.policy.labelKey,
    state: 'pending',
    attempts: 0,
    lastAttemptAt: null,
    nextAttemptAt: null,
    lastError: null,
    sealedBody: input.body === undefined ? null : await sealJson(key, input.body),
    sealedDetail: detail === null ? null : await sealJson(key, detail),
    idempotencyKey: globalThis.crypto?.randomUUID?.() ?? `varza-${now.toString(36)}`,
  };

  await offlineStore().put('outbox', record.id, record);
  return record;
}

/** The queue in the order it must be replayed. */
export async function listOutbox(): Promise<readonly OutboxRecord[]> {
  const entries = await offlineStore().entries<OutboxRecord>('outbox');
  return entries.map((entry) => entry.value);
}

export async function putOutbox(record: OutboxRecord): Promise<void> {
  await offlineStore().put('outbox', record.id, record);
}

export async function removeOutbox(id: string): Promise<void> {
  await offlineStore().remove('outbox', id);
}

export async function clearOutbox(): Promise<void> {
  await offlineStore().clear('outbox');
}

export interface OutboxCounts {
  readonly pending: number;
  readonly failed: number;
  readonly rejected: number;
  readonly uncertain: number;
  readonly total: number;
  /** Everything still waiting to reach the server: what the badge counts. */
  readonly unsent: number;
  /** Everything a person has to look at: what turns the badge into a warning. */
  readonly needsAttention: number;
}

export function countOutbox(records: readonly OutboxRecord[]): OutboxCounts {
  const of = (state: OutboxState) => records.filter((record) => record.state === state).length;
  const pending = of('pending');
  const failed = of('failed');
  const rejected = of('rejected');
  const uncertain = of('uncertain');

  return {
    pending,
    failed,
    rejected,
    uncertain,
    total: records.length,
    unsent: pending + failed,
    needsAttention: rejected + uncertain,
  };
}

export async function openOutboxBody<T>(record: OutboxRecord): Promise<T | null> {
  const key = currentDataKey();
  if (key === null || record.sealedBody === null) return null;
  try {
    return await openJson<T>(key, record.sealedBody);
  } catch {
    return null;
  }
}

export async function openOutboxDetail(record: OutboxRecord): Promise<QueuedDetail | null> {
  const key = currentDataKey();
  if (key === null || record.sealedDetail === null) return null;
  try {
    return await openJson<QueuedDetail>(key, record.sealedDetail);
  } catch {
    return null;
  }
}
